import { EParameterTypes, ModalType } from "@/enums";
import { NotRequiredIcon, RequiredIcon, SettingIcon } from "../../icons";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  OutputTranslationsFields,
  buildTranslationsPayload,
  createTranslationRow,
} from "@/widgets/outputs-admin/components/output-translations-fields";
import { useTranslation } from "react-i18next";
import { displayParameterUnit } from "@/utils/parameter-unit-label";
import { UpdateParameterModal } from "./update-parameter-modal";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { EHttpMethod } from "@/services/api-service/enums";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { selectedUpdateTypeState } from "@/store/selected-update-type";
import { doNotAskNextTimeState } from "@/store";
import { GoMakeDeleteModal, GomakePrimaryButton, SecondSwitch } from "@/components";
import {
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  LockIcon,
  LockOpenIcon,
  PlusIcon,
} from "@/icons";
import { useRouter } from "next/router";
import { AddNewParameterModal } from "./add-new-parameter-modal";
import { SelectExistingParameterModal } from "./select-existing-parameter-modal";
import { DeleteIcon } from "@/widgets/settings-mailing/messageTemplates/components/more-circle/icons/delete";
import { EditIcon } from "@/icons";
import { useModal } from "@/hooks/use-modal";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";
import { GmIcon, type GmIconName } from "@/themes/modern/components/gm-icon";
import { LinkIcon } from "@/icons/shared/link-icon";
import { AIIcon } from "@/icons/side-list-icons/ai";
import { GomakeTextInput } from "@/components";
import { userState } from "@/store";
import { UNIT_TYPE } from "@/enums";
import { isPrintingColorsParameter } from "@/widgets/shared-admin-customers/digital-offset-price/multi-parameter-modal/printing-colors-preset-utils";
import { isRollDirectionParameter } from "@/widgets/shared-admin-customers/digital-offset-price/multi-parameter-modal/roll-direction-utils";

export enum UpdateTypes {
  PRODUCT_ONLY = 0,
  SAME_PRICING_TYPE,
  ALL_PRODUCTS,
}

const SECTION_ICONS = new Set<GmIconName>([
  "user", "calendar", "truck", "phone", "mail", "pin", "plus", "minus", "x", "trash",
  "save", "send", "printer", "export", "more", "chevron", "search", "file", "note",
  "lock", "coins", "tag", "receipt", "calculator", "pencil", "expand", "layers",
  "square", "clipboard", "briefcase", "arrow-left", "arrow-right",
]);

const SECTION_ICON_FALLBACK: GmIconName[] = [
  "clipboard",
  "square",
  "layers",
  "printer",
];

const pickSectionIcon = (name: string | undefined, index: number): GmIconName => {
  const n = (name || "").toLowerCase();
  if (/size|shape|dimension|format|גודל|צורה/.test(n)) return "square";
  if (/material|paper|sheet|stock|חומר|נייר/.test(n)) return "layers";
  if (/print|color|colour|side|הדפסה|צבע/.test(n)) return "printer";
  if (/finish|lamin|cut|fold|bind|גימור/.test(n)) return "tag";
  if (/info|product|general|quantity|category|מוצר|כללי/.test(n)) return "clipboard";
  return SECTION_ICON_FALLBACK[index % SECTION_ICON_FALLBACK.length];
};

enum ParameterActionType {
  VISIBLE = "VISIBLE",
  REQUIRED = "REQUIRED",
  EDITABLE = "EDITABLE",
  SHOW_IN_DETAILS = "SHOW_IN_DETAILS",
  AI_SUGGESTION = "AI_SUGGESTION",
}

const TemplateParameterSettingsDialog = ({
  parameter,
  onClose,
  onSave,
}: {
  parameter: any;
  onClose: () => void;
  onSave: (parameter: any) => void;
}) => {
  const { t } = useTranslation();
  const { alertWarning } = useSnackBar();
  const [isHidden, setIsHidden] = useState(Boolean(parameter?.isHidden));
  const [isRequired, setIsRequired] = useState(Boolean(parameter?.isRequired));
  const [isEditable, setIsEditable] = useState(parameter?.isLock !== true);
  const [isShowToClient, setIsShowToClient] = useState(parameter?.isShowToClient !== false);
  const [defaultUnit, setDefaultUnit] = useState(parameter?.defaultUnit || "");
  const [imperialUnit, setImperialUnit] = useState(parameter?.imperialUnit || "");
  const showAiSuggestions =
    parameter?.parameterType === EParameterTypes.INPUT_NUMBER ||
    parameter?.parameterType === EParameterTypes.INPUT_TEXT;
  const [aiSuggestions, setAiSuggestions] = useState<string[]>(
    Array.isArray(parameter?.aiSuggestionValues) && parameter.aiSuggestionValues.length
      ? parameter.aiSuggestionValues.map((item: any) => String(item ?? ""))
      : [""]
  );
  const [translationRows, setTranslationRows] = useState(() => {
    const existing = Array.isArray(parameter?.translations) ? parameter.translations : [];
    return existing.length
      ? existing.map((item: { lang: string; name: string }) => createTranslationRow(item.lang, item.name))
      : [createTranslationRow()];
  });

  const toggleCardSx = {
    border: "1px solid #E6E8F0",
    borderRadius: "12px",
    px: 2,
    py: 1.25,
    m: 0,
    background: "#fff",
    justifyContent: "space-between",
  };
  const fieldLabelSx = { fontSize: 14, fontWeight: 600, color: "#1F2430" };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, pr: 1 }}>
        {`${t("materialsConfigAdmin.edit")} ${parameter?.name || ""}`}
        <IconButton aria-label={t("modal.close")} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 0.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <FormControlLabel
              sx={toggleCardSx}
              labelPlacement="start"
              control={
                <Switch
                  checked={isHidden}
                  onChange={(_event, checked) => {
                    if (checked && parameter?.defaulValueIsRequiredBeforeHide && !parameter?.defaultValue) {
                      alertWarning("products.parameters.setDefaultValue");
                      return;
                    }
                    setIsHidden(checked);
                  }}
                />
              }
              label={t("tooltips.hidden")}
            />
            {parameter?.parameterType !== EParameterTypes.SWITCH && (
              <FormControlLabel
                sx={toggleCardSx}
                labelPlacement="start"
                control={
                  <Switch
                    checked={isRequired}
                    onChange={(_event, checked) => {
                      if (!checked && parameter?.isRequiredInTemplate) {
                        alertWarning("products.parameters.isRequiredInTemplate");
                        return;
                      }
                      setIsRequired(checked);
                    }}
                  />
                }
                label={t("tooltips.required")}
              />
            )}
            <FormControlLabel
              sx={toggleCardSx}
              labelPlacement="start"
              control={<Switch checked={isEditable} onChange={(_event, checked) => setIsEditable(checked)} />}
              label={t("tooltips.editable")}
            />
            <FormControlLabel
              sx={toggleCardSx}
              labelPlacement="start"
              control={<Switch checked={isShowToClient} onChange={(_event, checked) => setIsShowToClient(checked)} />}
              label={t("tooltips.shownInDetails")}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Stack gap={0.75}>
              <span style={fieldLabelSx}>{t("outputsAdmin.defaultUnit")}</span>
              <TextField
                size="small"
                placeholder={t("outputsAdmin.defaultUnit")}
                value={defaultUnit}
                onChange={(event) => setDefaultUnit(event.target.value)}
              />
            </Stack>
            <Stack gap={0.75}>
              <span style={fieldLabelSx}>{t("outputsAdmin.imperialUnit")}</span>
              <TextField
                size="small"
                placeholder={t("outputsAdmin.imperialUnit")}
                value={imperialUnit}
                onChange={(event) => setImperialUnit(event.target.value)}
              />
            </Stack>
          </Box>
          {showAiSuggestions && (
            <Stack gap={0.75}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <span style={fieldLabelSx}>{t("products.parameters.aiSuggestion")}</span>
                <IconButton
                  size="small"
                  aria-label={t("materials.buttons.add")}
                  onClick={() => {
                    if (aiSuggestions.some((value) => !String(value ?? "").trim())) return;
                    setAiSuggestions([...aiSuggestions, ""]);
                  }}
                  sx={{ color: "#5D55E7" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
              {aiSuggestions.map((value, index) => (
                <Stack key={`ai-${index}`} direction="row" gap={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    type={parameter?.parameterType === EParameterTypes.INPUT_NUMBER ? "number" : "text"}
                    placeholder={t("products.parameters.aiSuggestion")}
                    value={value}
                    onChange={(event) => {
                      const next = [...aiSuggestions];
                      next[index] = event.target.value;
                      setAiSuggestions(next);
                    }}
                  />
                  <IconButton
                    size="small"
                    aria-label={t("modal.delete")}
                    onClick={() => setAiSuggestions(aiSuggestions.filter((_, itemIndex) => itemIndex !== index))}
                    sx={{ color: "#D32F2F", flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
          <OutputTranslationsFields layout="picker" rows={translationRows} onChange={setTranslationRows} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("modal.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              ...parameter,
              isHidden,
              isRequired,
              isLock: !isEditable,
              isShowToClient,
              defaultUnit,
              imperialUnit,
              aiSuggestionValues: showAiSuggestions
                ? aiSuggestions.map((value) => String(value ?? "").trim()).filter(Boolean)
                : parameter?.aiSuggestionValues,
              translations: buildTranslationsPayload(translationRows),
            })
          }
        >
          {t("materials.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SectionMappingWidget = ({
  index,
  classes,
  subSection,
  onOpenModal,
  onOpenExternalIdsModal,
  section,
  updatedProductParameterHidden,
  updatedProductParameterAiSuggestion,
  updatedProductParameteRequierd,
  _renderParameterType,
  relatedParameters,
  updatedProductParameteLocked,
  updatedProductParameterShowToClient,
  getProductById,
  removeProductParamete,
  saveTemplateParameterSettings,
  updatedProductParameterTranslations,
  queueAddExistingParameterToTemplate,
  onEditSubSection,
  deleteSubSectionFromTemplate,
  isVariationProductMode,
  onOpenColorOptionsModal,
  onOpenRollDirectionOptionsModal,
  insideAccordion = false,
  accordionActionsEl = null,
}: {
  index: any;
  classes: any;
  subSection: any;
  onOpenModal: any;
  onOpenExternalIdsModal: any;
  section: any;
  updatedProductParameterHidden: any;
  updatedProductParameterAiSuggestion: any;
  updatedProductParameteRequierd: any;
  _renderParameterType: any;
  relatedParameters: any;
  updatedProductParameteLocked: any;
  updatedProductParameterShowToClient: any;
  getProductById: any;
  removeProductParamete: any;
  saveTemplateParameterSettings?: (sectionId: string, subSectionId: string, parameter: any) => void;
  updatedProductParameterTranslations?: (
    sectionId: string,
    subSectionId: string,
    parameter: any,
    translations: { lang: string; name: string }[]
  ) => void;
  queueAddExistingParameterToTemplate?: (sectionId: string, subSectionId: string, parameter: any) => boolean;
  onEditSubSection?: (subSection: any) => void;
  deleteSubSectionFromTemplate?: (sectionId: string, subSectionId: string) => void;
  isVariationProductMode: any;
  onOpenColorOptionsModal: any;
  onOpenRollDirectionOptionsModal: any;
  insideAccordion?: boolean;
  accordionActionsEl?: HTMLDivElement | null;
}) => {
  const { t } = useTranslation();
  const isModern = useTheme().currentTheme === "modern";
  const showModernCard = isModern && !insideAccordion;
  const { callApi } = useGomakeAxios();
  const { alertFault, alertWarning, alertSuccessDelete, alertFaultDelete } =
    useSnackBar();
  const { secondColor } = useGomakeTheme();
  const router = useRouter();
  const isTemplateEdit = router.asPath?.includes("/settings/templates/");
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [parameterSettings, setParameterSettings] = useState<any>(null);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState(null);
  const [selectedUpdateType, setSelectedUpdateType] = useRecoilState(
    selectedUpdateTypeState,
  );
  const { isOpen, open: openModal, close: closeModal } = useModal();
  const [doNotAskNextTime, setDoNotAskNextTime] = useRecoilState(
    doNotAskNextTimeState,
  );
  const user = useRecoilValue(userState);
  const unitType = (user as any)?.printHouseProfile?.unitType || 0;
  const [isAddNewParameterModalOpen, setIsAddNewParameterModalOpen] =
    useState(false);
  const [aiAnchorEl, setAiAnchorEl] = useState<HTMLElement | null>(null);
  const [aiDraftValues, setAiDraftValues] = useState<string[]>([]);
  const aiSuggestionListRef = useRef<string[]>([]);
  const [isDeleteSubSectionOpen, setIsDeleteSubSectionOpen] = useState(false);
  const handleCloseAddNewParameterModal = () => {
    setIsAddNewParameterModalOpen(false);
  };
  const handleOpenAddNewParameterModal = () => {
    setIsAddNewParameterModalOpen(true);
  };
  const [newParameterState, setNewParameterState] = useState({
    parameterName: "",
    parameterType: 0,
    productId: "",
    sectionId: "",
    subSectionId: "",
    updateType: 0,
  });

  const onChangeInputs = (key: string, value: any) => {
    setNewParameterState({ ...newParameterState, [key]: value });
  };

  const renderIconWithTooltip = (
    condition,
    trueIcon,
    falseIcon,
    trueTitle,
    falseTitle,
    onClickHandler,
  ) => (
    <Tooltip
      title={
        condition ? t(`tooltips.${trueTitle}`) : t(`tooltips.${falseTitle}`)
      }
      placement="top"
    >
      <div style={{ cursor: "pointer" }} onClick={onClickHandler}>
        {condition ? trueIcon : falseIcon}
      </div>
    </Tooltip>
  );

  const handleCloseModal = () => {
    setIsUpdateModalOpen(false);
    // setSelectedUpdateType(null);
  };

  const handleOpenModal = () => {
    if (isVariationProductMode || isTemplateEdit) {
      return;
    }
    if (!doNotAskNextTime) {
      setIsUpdateModalOpen(true);
    }
  };

  const toAiSuggestionList = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item ?? ""));
    }
    if (value === null || value === undefined || value === "") {
      return [];
    }
    return [String(value)];
  };

  const saveAiSuggestions = () => {
    const isNumber =
      selectedParameter?.parameterType === EParameterTypes.INPUT_NUMBER;
    const trimmed = aiDraftValues.map((value) => (value ?? "").trim());
    if (
      isNumber &&
      trimmed.some((value) => value !== "" && !Number.isFinite(Number(value)))
    ) {
      alertFault(t("products.parameters.aiSuggestionNumberInvalid"));
      return;
    }
    const nextValues = trimmed.filter((value) => value !== "");
    aiSuggestionListRef.current = nextValues;
    if (!doNotAskNextTime && !isVariationProductMode && !isTemplateEdit) {
      setSelectedActionType(ParameterActionType.AI_SUGGESTION);
      handleOpenModal();
      return;
    }
    handleUpdate(
      ParameterActionType.AI_SUGGESTION,
      selectedParameter,
      isVariationProductMode ? UpdateTypes.PRODUCT_ONLY : selectedUpdateType,
    );
  };

  const hasDefaultValue = (parameter) => {
    // Check for DROP_DOWN_LIST, SELECT_MATERIALS, or other specific types
    if (
      parameter?.parameterType === EParameterTypes.DROP_DOWN_LIST ||
      parameter?.parameterType === EParameterTypes.SELECT_MATERIALS
    ) {
      // Check valuesConfigs for default value
      return parameter?.valuesConfigs?.some(
        (config) => config.isDefault === true,
      );
    }
    // For other parameter types, check defaultValue
    return !!parameter?.defaultValue;
  };

  const canUpdateVisibility = (parameter) => {
    if (!parameter?.defaulValueIsRequiredBeforeHide) {
      return true;
    }
    return hasDefaultValue(parameter);
  };

  const handleUpdate = (
    actionType: ParameterActionType,
    parameter: any,
    updateType?: UpdateTypes,
  ) => {
    const resolvedScopeType = isVariationProductMode || isTemplateEdit
      ? UpdateTypes.PRODUCT_ONLY
      : updateType !== null && updateType !== undefined
        ? updateType
        : selectedUpdateType;
    if (resolvedScopeType === null || resolvedScopeType === undefined) {
      alertFault(t("products.parameters.pleaseSelectOneOption"));
      return;
    }
    switch (actionType) {
      case ParameterActionType.VISIBLE:
        updatedProductParameterHidden(
          section?.id,
          subSection?.id,
          parameter,
          resolvedScopeType,
        );
        break;
      case ParameterActionType.REQUIRED:
        updatedProductParameteRequierd(
          section?.id,
          subSection?.id,
          parameter,
          resolvedScopeType,
        );
        break;
      case ParameterActionType.EDITABLE:
        updatedProductParameteLocked(
          section?.id,
          subSection?.id,
          parameter,
          resolvedScopeType,
        );
        break;
      case ParameterActionType.SHOW_IN_DETAILS:
        updatedProductParameterShowToClient(
          section?.id,
          subSection?.id,
          parameter,
          resolvedScopeType,
        );
        break;
      case ParameterActionType.AI_SUGGESTION:
        updatedProductParameterAiSuggestion?.(
          section?.id,
          subSection?.id,
          parameter,
          aiSuggestionListRef.current,
          resolvedScopeType,
        );
        setAiAnchorEl(null);
        break;
      default:
        break;
    }
    handleCloseModal();
  };

  const getParameterSelectedValueIds = (parameter: any): string[] => {
    if (!parameter) {
      return [];
    }

    const defaultValues: string[] = [];

    if (parameter?.defaultValue !== null && parameter?.defaultValue !== undefined) {
      const normalizedDefaultValue = String(parameter.defaultValue).trim();
      if (normalizedDefaultValue) {
        defaultValues.push(normalizedDefaultValue);
      }
    }

    const isMaterialParameterType =
      parameter?.parameterType === EParameterTypes.SELECT_MATERIALS ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_CATEGORY ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_NAME;

    if (Array.isArray(parameter?.valuesConfigs)) {
      parameter.valuesConfigs.forEach((config: any) => {
        if (!config?.isDefault) {
          return;
        }

        // For non-material parameters, config.id represents selected option id.
        // For material parameters this id is internal config id and should not
        // be used to satisfy dependency relations.
        if (!isMaterialParameterType && config?.id) {
          defaultValues.push(String(config.id));
        }

        if (Array.isArray(config?.materialValueIds)) {
          // In variation mode we only treat material parameter as "filled"
          // when there is an explicit selected material value (set from UI).
          if (isMaterialParameterType) {
            const hasExplicitSelectedMaterialValue =
              config?.value !== null &&
              config?.value !== undefined &&
              String(config.value).trim().length > 0;
            if (!hasExplicitSelectedMaterialValue) {
              return;
            }
          }

          config.materialValueIds.forEach((materialItem: any) => {
            const materialValueId =
              materialItem?.valueId !== null && materialItem?.valueId !== undefined
                ? String(materialItem.valueId).trim()
                : "";
            if (materialValueId) {
              defaultValues.push(materialValueId);
            }
          });
        }
      });
    }

    return Array.from(new Set(defaultValues));
  };

  const isRelationSatisfied = (parameter: any, relation: any): boolean => {
    const selectedValueIds = getParameterSelectedValueIds(parameter);
    if (!selectedValueIds.length) {
      return false;
    }

    if (relation?.activateByAllValues) {
      return true;
    }

    const relationSelectedValueIds = Array.isArray(relation?.selectedValueIds)
      ? relation.selectedValueIds.map((value: any) => String(value))
      : [];
    const relationNotSelectedValueIds = Array.isArray(relation?.notSelectedValueIds)
      ? relation.notSelectedValueIds.map((value: any) => String(value))
      : [];

    const hasSelectedMatch =
      relationSelectedValueIds.length === 0 ||
      selectedValueIds.some((valueId) => relationSelectedValueIds.includes(valueId));
    const hasNotSelectedMatch =
      relationNotSelectedValueIds.length === 0 ||
      selectedValueIds.every((valueId) => !relationNotSelectedValueIds.includes(valueId));

    return hasSelectedMatch && hasNotSelectedMatch;
  };

  const visibleParameters = useMemo(() => {
    const parameters =
      subSection?.parameters?.filter((parameter: any) => !parameter?.isHiddenInSetting) ??
      [];

    if (!isVariationProductMode) {
      return parameters;
    }

    if (parameters.length === 0) {
      return parameters;
    }

    const parameterById = new Map(
      parameters.map((parameter: any) => [parameter?.id, parameter]),
    );
    const incomingRelationsByTarget = new Map<
      string,
      Array<{ sourceId: string; relation: any }>
    >();

    parameters.forEach((parameter: any) => {
      const parameterRelations = Array.isArray(parameter?.relatedParameters)
        ? parameter.relatedParameters
        : [];

      parameterRelations.forEach((relation: any) => {
        const targetId = relation?.parameterId;
        if (!targetId || !parameterById.has(targetId)) {
          return;
        }

        const existingIncomingRelations = incomingRelationsByTarget.get(targetId) ?? [];
        existingIncomingRelations.push({
          sourceId: parameter.id,
          relation,
        });
        incomingRelationsByTarget.set(targetId, existingIncomingRelations);
      });
    });

    if (incomingRelationsByTarget.size === 0) {
      return parameters;
    }

    const visibleParameterIds = new Set<string>();

    parameters.forEach((parameter: any) => {
      if (!incomingRelationsByTarget.has(parameter.id)) {
        visibleParameterIds.add(parameter.id);
      }
    });

    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;

      incomingRelationsByTarget.forEach((relations, targetId) => {
        if (visibleParameterIds.has(targetId)) {
          return;
        }

        const shouldShowTarget = relations.some(({ sourceId, relation }) => {
          if (!visibleParameterIds.has(sourceId)) {
            return false;
          }

          const sourceParameter = parameterById.get(sourceId);
          if (!sourceParameter) {
            return false;
          }

          return isRelationSatisfied(sourceParameter, relation);
        });

        if (shouldShowTarget) {
          visibleParameterIds.add(targetId);
          hasChanges = true;
        }
      });
    }

    return parameters.filter((parameter: any) => visibleParameterIds.has(parameter.id));
  }, [subSection?.parameters, isVariationProductMode]);

  const addParameterButton = (
    <Tooltip title={t("products.parameters.addNewParameter")} placement="top">
      <IconButton
        onClick={() => {
          handleOpenAddNewParameterModal();
          setNewParameterState({
            productId: router?.query?.id as string,
            sectionId: section?.id,
            subSectionId: subSection?.id,
            updateType: 0,
            parameterName: "",
            parameterType: 0,
          });
        }}
      >
        <PlusIcon stroke={`var(--theme-accent-color, ${secondColor(500)})`} />
      </IconButton>
    </Tooltip>
  );

  return (
    <div
      key={index}
      className={showModernCard ? "gm-pc-card" : undefined}
      style={showModernCard ? { width: "100%" } : classes.subSectionContainer}
    >
      {showModernCard ? (
        <div className="gm-pc-card-title">
          <GmIcon name={SECTION_ICONS.has(section?.icon) ? section.icon : pickSectionIcon(subSection?.name, index)} />
          <span>{subSection.name}</span>
          {isTemplateEdit && onEditSubSection && (
            <IconButton size="small" onClick={() => onEditSubSection(subSection)}>
              <EditIcon stroke="#5D55E7" />
            </IconButton>
          )}
          <span className="gm-settings-card-add">{addParameterButton}</span>
        </div>
      ) : (
        (!isModern || !insideAccordion) && (
          <div style={classes.subSectionTitleStyle}>
            {subSection.name}
            {addParameterButton}
          </div>
        )
      )}
      {insideAccordion && accordionActionsEl
        ? createPortal(addParameterButton, accordionActionsEl)
        : null}
      {doNotAskNextTime && index === 0 && !isVariationProductMode && !isTemplateEdit && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: "500" }}>
            {t("products.parameters.samePricingType")}
          </div>
          <SecondSwitch
            defaultChecked={
              selectedUpdateType === UpdateTypes.SAME_PRICING_TYPE
            }
            checked={selectedUpdateType === UpdateTypes.SAME_PRICING_TYPE}
            onChange={(e: any, value: any) => {
              setSelectedUpdateType(
                value
                  ? UpdateTypes.SAME_PRICING_TYPE
                  : UpdateTypes.PRODUCT_ONLY,
              );
            }}
            key={selectedUpdateType}
          />
        </div>
      )}
      <div style={classes.subSectionTitleStyle}>
        {subSection.name}
        {isTemplateEdit && onEditSubSection && (
          <IconButton size="small" onClick={() => onEditSubSection(subSection)}>
            <EditIcon stroke="#5D55E7" />
          </IconButton>
        )}
        {isTemplateEdit && (
          <IconButton onClick={() => setIsDeleteSubSectionOpen(true)}>
            <DeleteIcon />
          </IconButton>
        )}
        {!insideAccordion && addParameterButton}
      </div>

      <div style={classes.parametersContainer}>
        {visibleParameters?.map((parameter, paramIndex) => {
            const ShowEditExternalCodeValue =
              parameter?.parameterType ===
                EParameterTypes.SELECT_CHILDS_PARAMETERS ||
              parameter?.parameterType === EParameterTypes.SELECT_MATERIALS ||
              parameter?.parameterType === EParameterTypes.DROP_DOWN_LIST ||
              parameter?.parameterType ===
                EParameterTypes.ADVERTISING_PRODUCT_CATEGORY ||
              parameter?.parameterType ===
                EParameterTypes.ADVERTISING_PRODUCT_NAME ||
              parameter?.parameterType === EParameterTypes.SWITCH;
            return (
              <div key={paramIndex}>
                <div style={classes.parameterContainer}>
                  <div style={classes.parameterLabelStyle}>
                    <div style={classes.paramNameStyle}>
                      {parameter?.name}
                      <small>
                        &nbsp;
                        {displayParameterUnit(
                          unitType === UNIT_TYPE.IMPERIAL
                            ? parameter?.imperialUnit || parameter?.defaultUnit
                            : parameter?.defaultUnit
                        )}
                      </small>
                    </div>
                    {isTemplateEdit && (
                      <Tooltip title={t("materialsConfigAdmin.edit")} placement="top">
                        <IconButton
                          size="small"
                          aria-label={t("materialsConfigAdmin.edit")}
                          onClick={() => setParameterSettings(parameter)}
                          sx={{ padding: "2px" }}
                        >
                          <EditIcon stroke="#5D55E7" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isTemplateEdit && parameter?.parameterType ===
                      EParameterTypes.SELECT_CHILDS_PARAMETERS && (
                      <div
                        style={classes.plusIconStyle}
                        onClick={() =>
                          onOpenModal(parameter, section?.id, subSection?.id)
                        }
                      >
                        <SettingIcon width={20} height={20} />
                      </div>
                    )}
                    {!isTemplateEdit && isPrintingColorsParameter(parameter) && (
                      <Tooltip
                        title={t("products.offsetPrice.admin.options", {
                          defaultValue: "options",
                        })}
                        placement="top"
                      >
                        <div
                          style={classes.plusIconStyle}
                          onClick={() =>
                            onOpenColorOptionsModal(
                              parameter,
                              section?.id,
                              subSection?.id,
                            )
                          }
                        >
                          <SettingIcon width={20} height={20} />
                        </div>
                      </Tooltip>
                    )}
                    {!isTemplateEdit && isRollDirectionParameter(parameter) && (
                      <Tooltip
                        title={t("products.offsetPrice.admin.options", {
                          defaultValue: "options",
                        })}
                        placement="top"
                      >
                        <div
                          style={classes.plusIconStyle}
                          onClick={() =>
                            onOpenRollDirectionOptionsModal?.(
                              parameter,
                              section?.id,
                              subSection?.id,
                            )
                          }
                        >
                          <SettingIcon width={20} height={20} />
                        </div>
                      </Tooltip>
                    )}
                    {!isTemplateEdit && renderIconWithTooltip(
                      parameter?.isHidden,
                      <EyeOffIcon color={"#A4A4A4"} />,
                      <EyeIcon
                        color={`var(--theme-accent-color, ${secondColor(500)})`}
                      />,
                      "hidden",
                      "visible",
                      () => {
                        if (parameter?.defaulValueIsRequiredBeforeHide) {
                          if (!canUpdateVisibility(parameter)) {
                            alertWarning("products.parameters.setDefaultValue");
                            return;
                          }
                        }
                        if (!doNotAskNextTime && !isVariationProductMode && !isTemplateEdit) {
                          handleOpenModal();
                        } else {
                          handleUpdate(
                            ParameterActionType.VISIBLE,
                            parameter,
                            isVariationProductMode
                              ? UpdateTypes.PRODUCT_ONLY
                              : selectedUpdateType,
                          );
                        }
                        setSelectedActionType(ParameterActionType.VISIBLE);
                        setSelectedParameter(parameter);
                      },
                    )}
                    {!isTemplateEdit && parameter?.parameterType !== EParameterTypes.SWITCH &&
                      renderIconWithTooltip(
                        parameter?.isRequired,
                        <RequiredIcon
                          stroke={`var(--theme-accent-color, ${secondColor(
                            500,
                          )})`}
                        />,
                        <NotRequiredIcon />,
                        "required",
                        "notRequired",
                        () => {
                          if (parameter?.isRequiredInTemplate) {
                            alertWarning(
                              "products.parameters.isRequiredInTemplate",
                            );
                            return;
                          }
                          if (!doNotAskNextTime && !isVariationProductMode && !isTemplateEdit) {
                            handleOpenModal();
                          } else {
                            handleUpdate(
                              ParameterActionType.REQUIRED,
                              parameter,
                              isVariationProductMode
                                ? UpdateTypes.PRODUCT_ONLY
                                : selectedUpdateType,
                            );
                          }
                          setSelectedActionType(ParameterActionType.REQUIRED);
                          setSelectedParameter(parameter);
                        },
                      )}
                    {!isTemplateEdit && renderIconWithTooltip(
                      parameter?.isLock,
                      <LockIcon color={"#A4A4A4"} />,
                      <LockOpenIcon
                        color={`var(--theme-accent-color, ${secondColor(500)})`}
                      />,
                      "readOnly",
                      "editable",
                      () => {
                        if (!doNotAskNextTime && !isVariationProductMode && !isTemplateEdit) {
                          handleOpenModal();
                        } else {
                          handleUpdate(
                            ParameterActionType.EDITABLE,
                            parameter,
                            isVariationProductMode
                              ? UpdateTypes.PRODUCT_ONLY
                              : selectedUpdateType,
                          );
                        }
                        setSelectedActionType(ParameterActionType.EDITABLE);
                        setSelectedParameter(parameter);
                      },
                    )}
                    {!isTemplateEdit && renderIconWithTooltip(
                      parameter?.isShowToClient,
                      <InfoIcon
                        color={`var(--theme-accent-color, ${secondColor(500)})`}
                      />,
                      <InfoIcon color={"#A4A4A4"} />,
                      "shownInDetails",
                      "hiddenFromDetails",
                      () => {
                        if (!doNotAskNextTime && !isVariationProductMode && !isTemplateEdit) {
                          handleOpenModal();
                        } else {
                          handleUpdate(
                            ParameterActionType.SHOW_IN_DETAILS,
                            parameter,
                            isVariationProductMode
                              ? UpdateTypes.PRODUCT_ONLY
                              : selectedUpdateType,
                          );
                        }
                        setSelectedActionType(
                          ParameterActionType.SHOW_IN_DETAILS,
                        );
                        setSelectedParameter(parameter);
                      },
                    )}
                    {!isTemplateEdit && (
                    <div
                      style={classes.plusIconStyle}
                      onClick={() =>
                        onOpenExternalIdsModal(
                          parameter,
                          subSection?.parameters,
                          section?.id,
                          subSection?.id,
                        )
                      }
                    >
                      <LinkIcon
                        color={`var(--theme-accent-color, ${secondColor(500)})`}
                      />
                    </div>
                    )}
                    {!isTemplateEdit && (parameter?.parameterType === EParameterTypes.INPUT_NUMBER ||
                      parameter?.parameterType === EParameterTypes.INPUT_TEXT) && (
                      <Tooltip title={t("tooltips.aiSuggestion")} placement="top">
                        <div
                          style={{ cursor: "pointer", display: "flex" }}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={(event) => {
                            setSelectedParameter(parameter);
                            const existingSuggestions = toAiSuggestionList(
                              parameter?.aiSuggestionValues,
                            );
                            setAiDraftValues(
                              existingSuggestions.length
                                ? existingSuggestions
                                : [""],
                            );
                            setAiAnchorEl(event.currentTarget);
                          }}
                        >
                          <AIIcon
                            width={20}
                            height={20}
                            color={
                              toAiSuggestionList(parameter?.aiSuggestionValues)
                                .length
                                ? `var(--theme-accent-color, ${secondColor(500)})`
                                : "#A4A4A4"
                            }
                          />
                        </div>
                      </Tooltip>
                    )}
                    {(isTemplateEdit || parameter?.isDeletable) && (
                      <Tooltip title={t("modal.delete")} placement="top">
                        <div
                          style={classes.plusIconStyle}
                          onClick={() => {
                            setSelectedParameter(parameter);
                            openModal();
                          }}
                        >
                          <DeleteIcon
                            stroke={`var(--theme-accent-color, ${secondColor(
                              500,
                            )})`}
                            width={20}
                            height={20}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                  <div style={classes.renderParameterTypeContainer}>
                    {_renderParameterType(
                      section?.id,
                      subSection?.id,
                      parameter,
                      subSection?.parameters,
                      paramIndex,
                      section,
                      subSection,
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <Popover
        open={Boolean(aiAnchorEl)}
        anchorEl={aiAnchorEl}
        onClose={() => setAiAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div style={{ padding: 12, width: 320 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {t("products.parameters.aiSuggestion")}
            </div>
            <div
              style={{ cursor: "pointer", display: "flex" }}
              onClick={() => {
                const lastValue = aiDraftValues[aiDraftValues.length - 1];
                if (aiDraftValues.length > 0 && !(lastValue ?? "").trim()) {
                  alertFault("there is a empty row");
                  return;
                }
                setAiDraftValues([...aiDraftValues, ""]);
              }}
            >
              <PlusIcon />
            </div>
          </div>
          {aiDraftValues.map((value, index) => (
            <div
              key={`ai-suggestion-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
              <GomakeTextInput
                style={{ width: "100%" }}
                type={
                  selectedParameter?.parameterType ===
                  EParameterTypes.INPUT_NUMBER
                    ? "number"
                    : "text"
                }
                placeholder={t("products.parameters.aiSuggestion")}
                value={value}
                onChange={(e: any) => {
                  const next = [...aiDraftValues];
                  next[index] = e.target.value;
                  setAiDraftValues(next);
                }}
              />
              </div>
              <Tooltip title={t("modal.delete")} placement="top">
                <div
                  style={{ cursor: "pointer", display: "flex" }}
                  onClick={() =>
                    setAiDraftValues(
                      aiDraftValues.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  <DeleteIcon
                    stroke={`var(--theme-accent-color, ${secondColor(500)})`}
                    width={18}
                    height={18}
                  />
                </div>
              </Tooltip>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <GomakePrimaryButton
              style={{ width: "auto", minWidth: 72, height: 34 }}
              onClick={saveAiSuggestions}
            >
              {t("materials.buttons.add")}
            </GomakePrimaryButton>
          </div>
        </div>
      </Popover>
      {!isVariationProductMode && (
        <UpdateParameterModal
          openModal={isUpdateModalOpen}
          onClose={handleCloseModal}
          selectedUpdateType={selectedUpdateType}
          setSelectedUpdateType={setSelectedUpdateType}
          onUpdate={() =>
            handleUpdate(
              selectedActionType,
              selectedParameter,
              selectedUpdateType,
            )
          }
          setDoNotAskNextTime={setDoNotAskNextTime}
          doNotAskNextTime={doNotAskNextTime}
        />
      )}
      {isTemplateEdit ? (
        <SelectExistingParameterModal
          openModal={isAddNewParameterModalOpen}
          onClose={handleCloseAddNewParameterModal}
          templateId={router?.query?.id as string}
          sectionId={section?.id}
          subSectionId={subSection?.id}
          getProductById={getProductById}
          onAddExisting={queueAddExistingParameterToTemplate}
        />
      ) : (
        <AddNewParameterModal
          openModal={isAddNewParameterModalOpen}
          onClose={handleCloseAddNewParameterModal}
          newParameterState={newParameterState}
          onChangeInputs={onChangeInputs}
          getProductById={getProductById}
        />
      )}
      {parameterSettings && (
        <TemplateParameterSettingsDialog
          parameter={parameterSettings}
          onClose={() => setParameterSettings(null)}
          onSave={(next) => {
            saveTemplateParameterSettings?.(section?.id, subSection?.id, next);
            setParameterSettings(null);
          }}
        />
      )}
      <GoMakeDeleteModal
        modalType={ModalType.DELETE}
        openModal={isOpen}
        onClose={closeModal}
        onClickDelete={() =>
          removeProductParamete(
            section?.id,
            subSection?.id,
            selectedParameter,
            isVariationProductMode
              ? UpdateTypes.PRODUCT_ONLY
              : selectedUpdateType,
          )
        }
      />
      <GoMakeDeleteModal
        modalType={ModalType.DELETE}
        openModal={isDeleteSubSectionOpen}
        onClose={() => setIsDeleteSubSectionOpen(false)}
        onClickDelete={async () => {
          setIsDeleteSubSectionOpen(false);
          if (deleteSubSectionFromTemplate) {
            deleteSubSectionFromTemplate(section?.id, subSection?.id);
            return;
          }
          const rawId = router?.query?.id;
          const templateId = Array.isArray(rawId) ? rawId[0] : rawId;
          const res = await callApi(
            EHttpMethod.DELETE,
            `/v1/printhouse-config/templates/delete-sub-section?templateId=${templateId}&&sectionId=${section?.id}&&subSectionId=${subSection?.id}`,
          );
          if (res?.success) {
            alertSuccessDelete();
            getProductById();
          } else {
            alertFaultDelete();
          }
        }}
      />
    </div>
  );
};

export { SectionMappingWidget };
